import { Button, type ButtonProps } from '@seen/ui'
import type { JSX, Ref } from 'react'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'

export interface ComboboxButtonOption<TValue extends string> {
  value: TValue
  label: string
  keywords?: string[]
}

export interface ComboboxButtonProps<TValue extends string> extends Omit<
  ButtonProps,
  'onChange' | 'value'
> {
  options: ComboboxButtonOption<TValue>[]
  value?: TValue | null
  onChange: (value: TValue) => void
  ref?: Ref<HTMLButtonElement>
  placeholder?: string
  // Controlled popover state, forwarded to Combobox — e.g. so a host hover
  // card can hold itself open while the dropdown is open.
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function ComboboxButton<TValue extends string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  open,
  onOpenChange,
  ...delegated
}: ComboboxButtonProps<TValue>): JSX.Element {
  const comboboxOptions = options.map((option) => ({
    value: option.value,
    label: (
      <ComboboxOption isSelected={value === option.value}>
        {option.label}
      </ComboboxOption>
    ),
    keywords: option.keywords ?? [option.label],
  }))

  return (
    <Combobox
      options={comboboxOptions}
      onSelect={onChange}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Button variant="outline" {...delegated}>
        {options.find((o) => o.value === value)?.label ?? placeholder}
      </Button>
    </Combobox>
  )
}
